import React, { useEffect, useRef, useState } from 'react'
import './styles.scss'
import useSize from './useSize'
import { scaleLinear, scaleTime } from 'd3-scale'
import { DateSlider } from 'components/DateSlider'
import { formatSwissDate } from 'components/Charts_Assets'

import { area } from 'd3-shape'
import { Translate } from 'aws-sdk'
import { recordExpression } from '@babel/types'
const format = new Intl.NumberFormat('de-CH')
const nF = a => format.format(a)
type DataPointType = {
  d: string
  [key: string]: number | string
}
export const Chart = ({
  data,
  selectedRange,
  isMobile = false,
  yDomain = [0, 10000],
  classname = '',
  chartLabel,
  chartLabelSub,
  id,
  yProp = 'v',
  selectRange
}: {
  data: DataPointType[]
  selectedRange: Date[]
  isMobile?: boolean
  classname?: string
  chartLabel?: string
  chartLabelSub?: string
  yDomain?: number[]
  id: number
  yProp?: string
  selectRange: (e: Date[], i: number) => void
}) => {
  const [labelBox, setLabelBox] = useState<DOMRect | undefined>()
  const [showInfoBox, setShowInfoBox] = useState(false)
  const [container, size] = useSize<SVGSVGElement>()

  const width = size.width
  const height = size.height

  const plotArea = {
    top: isMobile ? 10 : 30,
    right: width - 0,
    left: 0,
    bottom: height - 0
  }

  const ref = React.createRef<SVGSVGElement>()
  useEffect(() => {
    if (ref && ref.current) setLabelBox(ref.current.getBoundingClientRect())
  }, [ref.current])

  const yFunction = (d: DataPointType) => yAxisScale(d[yProp] as number) || 0
  const xFunction = (d: DataPointType) =>
    xAxisScale(new Date(d.d as string)) || 0

  const xAxisScale = React.useMemo(
    () =>
      scaleTime()
        .domain([new Date(data[0].d), new Date(data[data.length - 1].d)])
        .range([plotArea.left, plotArea.right]),
    [size, yDomain]
  )
  const yAxisScale = React.useMemo(
    () =>
      scaleLinear<number, number>()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size, yDomain]
  )

  const areaFunction = React.useMemo(
    () =>
      area<{ d: string; v: number; ave7: number }>()
        .x(xFunction)
        .y0(yAxisScale(0) || 0)
        .y1(yFunction),
    [size, yDomain]
  )

  const defaultDataPoint = {
    d: '2020-02-24',
    [yProp]: 0
  }

  const selectedRangeDataPoints = [
    data.find(f => f.d === toSwissDate(selectedRange[0])) || defaultDataPoint,
    data.find(f => f.d === toSwissDate(selectedRange[1])) || defaultDataPoint
  ]
  const selectedRangePointPos = [
    {
      x: xAxisScale(new Date(selectedRangeDataPoints[0].d)) || 0,
      y: yAxisScale(+selectedRangeDataPoints[0][yProp]) || 0
    },
    {
      x: xAxisScale(new Date(selectedRangeDataPoints[1].d)) || 0,
      y: yAxisScale(+selectedRangeDataPoints[1][yProp]) || 0
    }
  ]
  const farLeft = selectedRangePointPos[0].x < width * 0.1
  const farRight = selectedRangePointPos[1].x > width * 0.9

  return (
    <div className={'Chart ' + classname}>
      <svg ref={container}>
        <defs>
          <marker
            id='triangle'
            viewBox='0 0 12 12'
            refX='11'
            refY='6'
            markerUnits='strokeWidth'
            markerWidth='18'
            markerHeight='8'
            orient='auto'
          >
            <path d='M 0 0 L 12 6 L 0 12 z' className='arrow' />
          </marker>
        </defs>
        {selectedRange && (
          <>
            <polygon
              points={[
                [selectedRangePointPos[0].x, selectedRangePointPos[0].y],
                [selectedRangePointPos[1].x, selectedRangePointPos[1].y],
                [selectedRangePointPos[1].x, yAxisScale(0)],
                [selectedRangePointPos[0].x, yAxisScale(0)]
              ].join(' ')}
            />
            {!isMobile && (
              <>
                {' '}
                <line
                  className='tickline'
                  x1={xAxisScale(new Date('2021-01-01'))}
                  x2={xAxisScale(new Date('2021-01-01'))}
                  y1={yAxisScale.range()[0] + 20}
                  y2={yAxisScale.range()[0]}
                />
                <text
                  className='ticktext'
                  x={(xAxisScale(new Date('2021-01-01')) || 0) + 6}
                  y={yAxisScale.range()[0] + 20}
                >
                  2021
                </text>
              </>
            )}
            <path d={areaFunction(data)} className='curve' />
            <line
              className='base'
              x1={0}
              x2={width}
              y1={yAxisScale(0)}
              y2={yAxisScale(0)}
            />

            <line
              className='guide'
              x1={selectedRangePointPos[0].x}
              x2={selectedRangePointPos[0].x}
              y1={selectedRangePointPos[0].y}
              y2={yAxisScale(0)}
            />
            <line
              className='guide'
              x1={selectedRangePointPos[1].x}
              x2={selectedRangePointPos[1].x}
              y1={selectedRangePointPos[1].y}
              y2={yAxisScale(0)}
            />

            <line
              className='vector'
              x1={selectedRangePointPos[0].x}
              y1={selectedRangePointPos[0].y}
              x2={selectedRangePointPos[1].x}
              y2={selectedRangePointPos[1].y}
              markerEnd='url(#triangle)'
            />
            <circle
              cx={selectedRangePointPos[0].x}
              cy={selectedRangePointPos[0].y}
              r={5}
            />

            {/* LEFT */}
            <g
              transform={`translate(${selectedRangePointPos[0].x +
                (isMobile && farLeft ? -6 : -10)},${Math.min(
                (yAxisScale(0) || 0) - 10,
                selectedRangePointPos[0].y + +(isMobile ? 18 : 4)
              )})`}
            >
              <TextWithBG
                classname='value'
                y={-14}
                anchor={isMobile && farLeft ? 'end' : 'end'}
              >
                {selectedRangeDataPoints[0][yProp].toFixed(0)}
              </TextWithBG>

              {!isMobile && (
                <TextWithBG
                  classname='date'
                  anchor={isMobile && farLeft ? 'start' : 'end'}
                >
                  {formatSwissDate(selectedRangeDataPoints[0].d)}
                </TextWithBG>
              )}
            </g>
            <g
              transform={`translate(${selectedRangePointPos[1].x +
                (isMobile && farRight ? 4 : 8)},${Math.min(
                (yAxisScale(0) || 0) - 10,
                selectedRangePointPos[1].y + (isMobile ? 18 : 4)
              )})`}
            >
              {/* RIGHT VALUE */}
              <TextWithBG
                classname='value'
                y={-14}
                anchor={isMobile && farRight ? 'start' : 'start'}
              >
                {selectedRangeDataPoints[1][yProp].toFixed(0)}
              </TextWithBG>

              {!isMobile && (
                <>
                  {/* RIGHT DATE */}
                  <TextWithBG
                    classname='date'
                    anchor={farRight ? 'start' : 'start'}
                  >
                    {formatSwissDate(selectedRangeDataPoints[1].d)}
                  </TextWithBG>
                  {/* LINE BELOW CHANGE */}{' '}
                  <line
                    className='labelseparator bg'
                    x2={
                      change(
                        selectedRangeDataPoints[0][yProp],
                        selectedRangeDataPoints[1][yProp]
                      ).length *
                        9 +
                      20
                    }
                    y1={-30}
                    y2={-30}
                  />
                  <line
                    className='labelseparator'
                    x2={
                      change(
                        selectedRangeDataPoints[0][yProp],
                        selectedRangeDataPoints[1][yProp]
                      ).length *
                        9 +
                      20
                    }
                    y1={-30}
                    y2={-30}
                  />
                  {/*  CHANGE */}
                  <TextWithBG
                    classname='change'
                    y={-36}
                    anchor={isMobile ? 'end' : 'start'}
                  >
                    {change(
                      selectedRangeDataPoints[0][yProp],
                      selectedRangeDataPoints[1][yProp]
                    )}
                  </TextWithBG>
                </>
              )}
            </g>
            {isMobile && (
              <>
                <TextWithBG
                  classname='change'
                  x={xAxisScale.range()[1]}
                  y={isMobile ? -10 : -36}
                  anchor={isMobile ? 'end' : 'start'}
                >
                  {change(
                    selectedRangeDataPoints[0][yProp],
                    selectedRangeDataPoints[1][yProp]
                  )}
                </TextWithBG>
                {/* RIGHT DATE */}
                <TextWithBG
                  classname='date'
                  anchor={farRight ? 'start' : 'start'}
                  x={xAxisScale.range()[0]}
                  y={-10}
                >
                  {formatSwissDate(selectedRangeDataPoints[0].d) +
                    ' – ' +
                    formatSwissDate(selectedRangeDataPoints[1].d)}
                </TextWithBG>
              </>
            )}
            {chartLabel && (
              <TextWithBG
                ref={ref}
                classname='label'
                x={isMobile ? 0 : 0}
                y={isMobile ? -28 : height / 2}
                anchor="start"
              >
                {chartLabel}
              </TextWithBG>
            )}
            {labelBox && (
              <g
                className='info'
                transform={`translate(${labelBox.width + 18},${
                  isMobile ? -34 : height / 2 - 6
                })`}
              >
                <circle r={8} />
                <text textAnchor="middle" y={6}>
                  i
                </text>
                <rect
                  width={30}
                  height={30}
                  x={-15}
                  y={-15}
                  fill="#fff"
                  onMouseMove={() => setShowInfoBox(true)}
                  onMouseLeave={() => setShowInfoBox(false)}
                  onTouchStart={() => setShowInfoBox(true)}
                />
              </g>
            )}
            {/* {chartLabelSub && (
              <TextWithBG
                classname='labelsub'
                x={isMobile ? xAxisScale.range()[1] : 0}
                y={isMobile ? -28 : height / 2}
                anchor={isMobile ? 'end' : 'start'}
              >
                {chartLabelSub}
              </TextWithBG>
            )} */}
          </>
        )}
      </svg>

      {selectedRange && (
        <DateSlider
          id={id}
          selectRange={selectRange}
          selectedRange={selectedRange}
          range={[new Date(data[0].d), new Date(data[data.length - 1].d)]}
        />
      )}
      {chartLabelSub && showInfoBox && labelBox && (
        <div
          className='overlay'
          style={{ top: isMobile ? -8 + 'px' : height / 2 + 16 + 'px' }}
        >
          <div
            className='arrow-up'
            style={{ left: labelBox.width + 4 + 'px' }}
          />
          {chartLabelSub}
        </div>
      )}
    </div>
  )
}

const TextWithBG = React.forwardRef(
  (
    {
      x = 0,
      y = 0,
      classname,
      anchor,
      children
    }: {
      x?: number
      y?: number
      classname: string
      anchor: 'start' | 'end' | 'middle'
      children: string | React.ReactChild
    },
    ref
  ) => (
    <g transform={`translate(${x},${y})`}>
      <text className={classname + ' bg'} textAnchor={anchor}>
        {children}
      </text>
      <text className={classname} textAnchor={anchor} ref={ref}>
        {children}
      </text>
    </g>
  )
)

// ({x,y,class,anchor,children}:{x:number,y:number,class:string,anchor:"start"|"end"|"middle",children:string|React.ReactChild})=><g ></g>

const toSwissDate = (datestring: Date) => {
  return new Date(datestring).toISOString().split('T')[0]
}
const change = (a: number, b: number) => {
  const val = (b * 100) / a - 100
  if (+a.toFixed(0) === 0) return '+' + nF(100) + '%'
  if (+b.toFixed(0) === 0) return '-' + nF(100) + '%'
  if (val > 2000) return '>' + nF(2000) + '%'
  return (val > 0 ? '+' : '') + nF(+val.toFixed(0)) + '%'
}
